import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import type { SearchSubscriptionResponse } from '@//app/api/protected/subscriptions/search/[id]';
import type { FetchError } from '@//data/httpClient';
import useUpdateSearchSubscription from '@//data/subscriptions/search/useUpdateSearchSubscription';
import { useAuth } from '@//providers/AuthProvider';
import { ToastTypeEnum, useToast } from '@//providers/ToastProvider';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import { sanitizeParams } from '@//utils/vehicles-search/utils';

interface UseSearchSubscriptionHandler {
  searchSubscriptionId?: string;
  searchParams: URLSearchParams;
  searchSubscriptionQuery: UseQueryResult<SearchSubscriptionResponse, FetchError>;
  rootUrl: string;
}

const useSearchSubscriptionHandler = ({
  searchSubscriptionId,
  searchSubscriptionQuery,
  searchParams,
  rootUrl,
}: UseSearchSubscriptionHandler) => {
  const tToast = useTranslations('other.toast.subscription.search');
  const { initialized, userManagerInitialized } = useAuth();
  const updateSubscriptionMutation = useUpdateSearchSubscription();
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const initializedRef = useRef(initialized);
  initializedRef.current = initialized;

  const { addToast } = useToast();
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);
  useEffect(() => {
    if (searchSubscriptionQuery.isError) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('searchSubscriptionId');
      nextParams.delete('offset');
      updateUrl(sanitizeParams(nextParams));
      if (searchSubscriptionQuery.error.status === 404) {
        addToast({
          type: ToastTypeEnum.Warning,
          title: tToast('notFound.title'),
          description: tToast('notFound.description'),
        });
      } else {
        addToast({
          type: ToastTypeEnum.Warning,
          title: tToast('get.error.title'),
          description: tToast('get.error.description'),
        });
      }
    }
  }, [searchSubscriptionQuery.error]);

  useEffect(() => {
    if (searchSubscriptionQuery.isSuccess) {
      updateSubscriptionMutation.mutate({
        id: searchSubscriptionQuery.data.response.id,
        silent: true,
        body: {
          name: searchSubscriptionQuery.data.response.name,
          query: searchSubscriptionQuery.data.response.query,
        },
      });
    }
  }, [searchSubscriptionQuery.isSuccess]);

  useEffect(() => {
    if (userManagerInitialized && searchSubscriptionId) {
      timeoutRef.current = setTimeout(() => {
        if (!initializedRef.current) {
          const nextParams = new URLSearchParams(searchParamsRef.current);
          nextParams.delete('searchSubscriptionId');
          nextParams.delete('offset');
          addToast({
            type: ToastTypeEnum.Warning,
            title: tToast('mustBeSignedIn.title'),
            description: tToast('mustBeSignedIn.description'),
          });
          updateUrl(sanitizeParams(nextParams));
        }
      }, 2000);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [userManagerInitialized]);
};

export default useSearchSubscriptionHandler;
